import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import styles from './upiIdTextBox.module.css';
import errorIcon from '../../../assets/images/verticaltv/font-icon.svg';
import { useCreateCheckoutOrder } from '../../../api/createCheckoutOrder';
import { useInitiateSubscriptionCheckout } from '../../../api/initiateCheckout';
import { FbPixelEvent_AddPaymentInfo } from '../../../utils/FbPixel/fbPixel';
import { fireAddPaymentInfoEvent } from '../../../utils/SmartScriptAppFlyer/appsFlyerEvents';
import { CLEVERTAP_EVENTS } from '../../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../../utils/cleverTapHelper';
import { useTokenStore } from '../../../storage/tokenState';
import { useSubscriptionPaymentStore } from '../../../storage/subscriptionPaymentStore';

interface UpiIdTextBoxProps {
  onClose: () => void;
  amount: number; // Display amount (shown on button)
  apiAmount?: number; // API amount (sent to createCheckoutOrder API)
  planAmount: number; // Plan amount for initiateSubscriptionCheckout
  currentPlanId: string; // Plan ID for initiateSubscriptionCheckout
}

const UpiIdTextBox: React.FC<UpiIdTextBoxProps> = ({ onClose, amount, apiAmount, planAmount, currentPlanId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [upiId, setUpiId] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const { token } = useTokenStore();
    
    // Check if current route is a trial flow route
    const isTrialFlow = location.pathname.startsWith('/trial');
    
    const { initiateCheckoutAsync, isLoading: isCheckoutLoading } = useInitiateSubscriptionCheckout();
    const { createOrderAsync, isLoading: isCreatingOrder } = useCreateCheckoutOrder();

    const subscriptionPaymentDataCtx = useSubscriptionPaymentStore((state) => state.subscriptionPaymentDataCtx);

    const suggestedApps = [
        { name: 'Paytm', suffix: '@paytm' },
        { name: 'Axis', suffix: '@axl' },
        { name: 'PhonePe', suffix: '@ybl' },
        { name: 'ICICI', suffix: '@okicici' },
        { name: 'SBI', suffix: '@sbi' }
    ];

    // UPI ID validation regex
    const validateUpiId = (value: string): boolean => {
        // UPI ID format: username@bank (e.g., user@paytm, user123@ybl)
        const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;
        return upiRegex.test(value);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUpiId(value);
        
        // Reset validation on input change
        if (!isValid) {
            setIsValid(true);
            setErrorMessage('');
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
        
        // Validate on blur if there's a value
        if (upiId) {
            const valid = validateUpiId(upiId);
            setIsValid(valid);
            if (!valid) {
                setErrorMessage('Enter valid UPI ID');
            }
        }
    };

    const handleChipClick = (suffix: string, event: React.MouseEvent<HTMLButtonElement>) => {
        // Get current UPI ID value
        const currentValue = upiId || '';
        // Remove any existing suffix (everything after @) if present
        const baseName = currentValue.split('@')[0];
        const newValue = baseName + suffix;
        
        // Update UPI ID state
        setUpiId(newValue);
        
        // Validate the new value
        const valid = validateUpiId(newValue);
        setIsValid(valid);
        if (!valid) {
            setErrorMessage('Enter valid UPI ID');
        } else {
            setErrorMessage('');
        }
        
        // Remove focus from the button to prevent it from staying in active state
        if (event.currentTarget) {
            event.currentTarget.blur();
        }
    };

    const callJuspayApi = async (orderData: any, upiVpa: string) => {
        try {
            // Extract required fields from orderData
            const orderId = orderData.orderId;
            const customerId = orderData.customerId;
            
            // Get merchant_id from environment variable (production/staging) or use default
            const merchantId = process.env.REACT_APP_JUSPAY_MERCHANT_ID || 'zupeetwo';
            
            // Prepare form data for Juspay API
            const formData = new URLSearchParams();
            formData.append('order_id', orderId);
            formData.append('merchant_id', merchantId);
            formData.append('payment_method_type', 'UPI');
            formData.append('payment_method', 'UPI_COLLECT');
            formData.append('upi_vpa', upiVpa);
            formData.append('redirect_after_payment', 'true');
            formData.append('format', 'json');
            formData.append('should_create_mandate', 'true');
            formData.append('mandate_type', 'EMANDATE');

            console.log('Calling Juspay API with:', {
                order_id: orderId,
                merchant_id: merchantId,
                customer_id: customerId,
                upi_vpa: upiVpa
            });

            // Call Juspay API without x-routing-id header to avoid 400 error
            // The header might not be required or might be causing the issue
            const response = await axios.post(
                'https://payments.juspay.in/txns',
                formData.toString(),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }
            );

            console.log('Juspay API response:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error calling Juspay API:', error);
            if (error.response) {
                console.error('Juspay API error response:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });
            }
            // Don't throw error, just log it - payment can still proceed
            return null;
        }
    };

    const handlePayClick = async () => {
        if (!hasValue || !isValid || !planAmount || !currentPlanId) {
            console.error('Validation failed:', { hasValue, isValid, planAmount, currentPlanId });
            return;
        }

        // Use apiAmount for API call (planAmount for trial), fallback to amount for display
        const amountForApi = apiAmount || amount;

        // Track CleverTap event when user submits valid UPI ID
        const userId = token?.zupeeUserId?.toString() || '';
        // Extract UPI app name from UPI ID suffix (e.g., @paytm -> Paytm, @ybl -> PhonePe)
        const upiSuffix = upiId.split('@')[1] || '';
        const upiAppName = suggestedApps.find(app => app.suffix.includes(upiSuffix))?.name || upiSuffix || 'Unknown';
        
        trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_UPI_SUBMIT, {
            [CT_PARAMS.USER_ID]: userId,
            [CT_PARAMS.UPI_ID]: upiId,
            [CT_PARAMS.MANDATE_AMOUNT]: amountForApi,
            [CT_PARAMS.UPI_APP]: upiAppName
        });

        try {
            // Step 1: Initiate subscription checkout - MUST be called first
            console.log('Step 1: Initiating subscription checkout for UPI ID:', {
                amount: planAmount,
                planId: currentPlanId,
                upiId: upiId
            });

            // const checkoutResponse = await initiateCheckoutAsync({
            //     amount: planAmount,
            //     autoMandate: true,
            //     planId: currentPlanId,
            //     referralCouponApplied: false,
            //     shouldFetchCustomerDetails: true
            // });

            // console.log('Step 1 Response:', checkoutResponse);

            // if (!checkoutResponse?.response?.success) {
            //     console.error('Step 1 Failed: initiateSubscriptionCheckout failed', checkoutResponse);
            //     alert('Failed to initiate payment. Please try again.');
            //     return;
            // }

            // if (!checkoutResponse?.response?.data?.sessionId) {
            //     console.error('Step 1 Failed: No sessionId in response', checkoutResponse);
            //     alert('Failed to initiate payment. Please try again.');
            //     return;
            // }

            // const sessionId = checkoutResponse.response.data.sessionId;
            // console.log('Step 1 Success: Checkout initiated, sessionId:', sessionId);

            // Track Facebook Pixel AddPaymentInfo event when payment gateway is initiated
            FbPixelEvent_AddPaymentInfo();
            // Track AppsFlyer AddPaymentInfo event
            fireAddPaymentInfoEvent({ paymentMethod: 'UPI_COLLECT', page: 'upi_id_textbox' });

            // Step 2: Create checkout order - MUST be called after Step 1 completes
            console.log('Step 2: Creating checkout order with sessionId:', {
                displayAmount: amount,
                apiAmount: amountForApi,
                upiId: upiId,
                sessionId: subscriptionPaymentDataCtx.sessionIdCtx
            });

            const orderResponse = await createOrderAsync({
                amount: amountForApi,
                constraintId: subscriptionPaymentDataCtx.sessionIdCtx,
                customerVpa: upiId,
                instrumentType: 'upi_collect',
                isNewSavedCard: false,
                paymentMode: 'UPI_COLLECT'
            });

            console.log('Step 2 Response:', orderResponse);

            // Check if API returned UPI addition error
            if (orderResponse?.response?.upiAdditionError) {
                console.error('Step 2 Failed: UPI addition error', orderResponse.response.upiAdditionError);
                setIsValid(false);
                setErrorMessage(orderResponse.response.upiAdditionError);
                return;
            }

            if (orderResponse?.response?.success && orderResponse?.response?.data) {
                const orderData = orderResponse.response.data;
                console.log('Step 2 Success: Order created:', {
                    orderId: orderData.orderId,
                    customerId: orderData.customerId,
                    mandateUri: orderData.mandateFlowData?.mandateUri
                });
                
                // Step 3: Call Juspay API after successful order creation
                console.log('Step 3: Calling Juspay API');
                await callJuspayApi(orderData, upiId);
                
                // Step 4: Store order details and navigate to payment-waiting screen
                // Store orderId and amount in sessionStorage for payment-waiting screen
                sessionStorage.setItem('pendingOrderId', orderData.orderId);
                sessionStorage.setItem('pendingPaymentAmount', amountForApi.toString());
                // Store display amount separately to detect trial flow (₹2 for trial, same as amountForApi for paid)
                sessionStorage.setItem('pendingPaymentDisplayAmount', amount.toString());
                sessionStorage.setItem('mandateUriOpened', 'true');
                sessionStorage.setItem('isUpiCollectFlow', 'true'); // Flag to indicate UPI collect flow
                
                console.log('Step 4: Navigating to payment-waiting screen');
                
                // Determine the correct route based on trial flow
                const paymentWaitingRoute = isTrialFlow ? '/trial/payment-waiting' : '/payment-waiting';
                
                // Navigate to payment-waiting screen
                navigate(paymentWaitingRoute, {
                    state: {
                        orderId: orderData.orderId,
                        amount: amountForApi,
                        displayAmount: amount, // Pass display amount separately for trial flow detection
                        appName: 'UPI Collect'
                    }
                });
            } else {
                console.error('Step 2 Failed: Order creation failed', orderResponse);
                alert('Failed to create order. Please try again.');
            }
        } catch (error: any) {
            console.error('Error processing payment:', error);
            console.error('Error details:', {
                message: error?.message,
                response: error?.response?.data,
                stack: error?.stack
            });
            alert('Failed to process payment. Please try again.');
        }
    };

    const hasValue = upiId.length > 0;
    const isLabelFloating = isFocused || hasValue;

    // Handle paste event
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData('text');
        const valid = validateUpiId(pastedText);
        setIsValid(valid);
        if (!valid) {
            setErrorMessage('Enter valid UPI ID');
        } else {
            setErrorMessage('');
        }
    };

    // Handle Enter key press
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && hasValue && isValid && planAmount && currentPlanId) {
            handlePayClick();
        }
    };

    // Handle overlay click to close
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Prevent clicks inside wrapper from closing
    const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        console.log('UpiIdTextBox component mounted/rendered');
        return () => {
            document.body.style.overflow = '';
            console.log('UpiIdTextBox component unmounted');
        };
    }, []);

    const modalContent = (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.wrapper} onClick={handleWrapperClick}>
                <h2 className={styles.title}>Enter UPI ID</h2>
                
                <div className={`${styles.inputContainer} ${!isValid ? styles.inputContainerError : ''}`}>
                    <label 
                        className={`${styles.floatingLabel} ${isLabelFloating ? styles.floatingLabelActive : ''} ${!isValid ? styles.floatingLabelError : ''}`}
                    >
                        Your UPI ID
                    </label>
                    <input 
                        type="text" 
                        value={upiId}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleBlur}
                        onPaste={handlePaste}
                        onKeyDown={handleKeyDown}
                        className={styles.input}
                        placeholder=""
                    />
                    {!isValid && (
                        <img 
                            src={errorIcon} 
                            alt="Error" 
                            className={styles.errorIcon}
                        />
                    )}
                </div>
                
                {!isValid && errorMessage && (
                    <p className={styles.errorMessage}>{errorMessage}</p>
                )}
                
                <div className={styles.chips}>
                    {suggestedApps.map((app, index) => (
                        <button
                            key={index}
                            className={styles.chip}
                            onClick={(e) => handleChipClick(app.suffix, e)}
                        >
                            {app.suffix}
                        </button>
                    ))}
                </div>

                <button 
                    className={`${styles.payButton} ${(hasValue && isValid) ? styles.payButtonActive : ''}`}
                    disabled={!hasValue || !isValid || isCheckoutLoading || isCreatingOrder}
                    onClick={handlePayClick}
                >
                    {(isCheckoutLoading || isCreatingOrder) ? 'Processing...' : `Pay ₹${amount}`}
                </button>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default UpiIdTextBox;